// RET-870 TEMPORARY SCAFFOLD — remove when RET-908 ships.
//
// Persists the operator's "Atendido" acknowledgment across reloads using a
// single localStorage key per workspace. Each entry carries a creation
// timestamp; entries older than ACKNOWLEDGMENT_TTL_MS are dropped on read.
//
// This exists because RET-870 moved the PENDIENTE chip lifecycle in-tab only.
// Without persistence, a reload within the operator's active work window
// re-surfaces the chip and makes the acknowledgment feel flimsy. Two hours
// matches the operator's realistic active window — short enough that a lead
// nobody actually responded to will re-nag, long enough that a quick reload
// does not erase the click.
//
// When RET-908 wires the backend `needsAttention` flag to the real signal
// ("operator replied"), delete this file and replace the Pipe.hook usage
// with the proper backend-driven truth.

const ACKNOWLEDGMENT_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

const storageKey = (workspaceId: string) => `slb_acknowledged_leads:${workspaceId}`;

type Entry = Record<string, number>;

function parseEntry(raw: string | null): Entry {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeEntry(workspaceId: string, entry: Entry): void {
  const key = storageKey(workspaceId);
  try {
    if (Object.keys(entry).length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // QuotaExceededError / access denied — acknowledgment is an in-tab affordance.
    // Dropping the write keeps the current tab's Set authoritative; a reload will
    // simply re-surface the chip, which matches the "no persistence" fallback.
  }
}

export function loadAcknowledgedLeadIds(workspaceId: string): Set<string> {
  if (!workspaceId) return new Set();
  const stored = parseEntry(localStorage.getItem(storageKey(workspaceId)));
  const now = Date.now();
  const fresh: Set<string> = new Set();
  const kept: Entry = {};
  let prunedAny = false;
  for (const [leadId, timestamp] of Object.entries(stored)) {
    if (typeof timestamp !== 'number' || now - timestamp >= ACKNOWLEDGMENT_TTL_MS) {
      prunedAny = true;
      continue;
    }
    fresh.add(leadId);
    kept[leadId] = timestamp;
  }
  if (prunedAny) writeEntry(workspaceId, kept);
  return fresh;
}

export function addAcknowledgedLeadId(workspaceId: string, leadId: string): void {
  if (!workspaceId || !leadId) return;
  const current = parseEntry(localStorage.getItem(storageKey(workspaceId)));
  current[leadId] = Date.now();
  writeEntry(workspaceId, current);
}

export function removeAcknowledgedLeadId(workspaceId: string, leadId: string): void {
  if (!workspaceId || !leadId) return;
  const current = parseEntry(localStorage.getItem(storageKey(workspaceId)));
  if (!(leadId in current)) return;
  delete current[leadId];
  writeEntry(workspaceId, current);
}
